import { useState, useEffect, useRef } from 'react';
import { IoChevronDown, IoSearch } from 'react-icons/io5';
import './AssignDropdown.css';

interface AssignableUsers {
  userId: string;
  firstName: string;
  lastName: string;
}

interface Props {
  label: string;
  placeholder: string;
  users: AssignableUsers[];
  selectedUsers: string[];
  onSelectionChange: (selectedIds: string[]) => void;
}

function AssignUserDropdown({
  label,
  placeholder,
  users,
  selectedUsers,
  onSelectionChange
}: Props): JSX.Element {
  const assignDropdownRef = useRef<HTMLDivElement>(null);
  const [assignUserSearchValue, setAssignUserSearchValue] = useState('');
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);

  const handleClickOutAssignBox = (event: MouseEvent) => {
    if (
      assignDropdownRef.current &&
      !assignDropdownRef.current.contains(event.target as Node)
    ) {
      if (isAssignDropdownOpen) {
          setIsAssignDropdownOpen(false);
      }
    }
  };
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutAssignBox);
    return () => document.removeEventListener('mousedown', handleClickOutAssignBox);
  }, [isAssignDropdownOpen]);

  const filteredAssignedUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName}`
      .toLowerCase()
      .includes(assignUserSearchValue.toLowerCase())
  );

  const handleUserSelection = (userId: string) => {
    const newSelection = selectedUsers.includes(userId)
      ? selectedUsers.filter((id) => id !== userId)
      : [...selectedUsers, userId];
    onSelectionChange(newSelection);
  };

  const handleSelectAllUsers = () => {
    const allSelected = filteredAssignedUsers.length === selectedUsers.length;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(filteredAssignedUsers.map((user) => user.userId));
    }
  };

  const getSelectedUserNames = () => {
    if (selectedUsers.length === 0) {
      return placeholder;
    }
    
    return selectedUsers
      .map((userId) => {
        const user = users.find((u) => u.userId === userId);
        return user ? `${user.firstName} ${user.lastName}` : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="assign-row">
      <span className="assign-dropdown-label">{label}</span>
      <div className="assign-dropdown-input-box" ref={assignDropdownRef}>
        <div className="assign-dropdown-input" onClick={() => setIsAssignDropdownOpen(!isAssignDropdownOpen)}>
          <span>{getSelectedUserNames()}</span>
          <IoChevronDown
            className={`assign-dropdown-arrow ${isAssignDropdownOpen ? 'open-assign-dropdown' : ''}`}
          />
        </div>

        {isAssignDropdownOpen && (
          <div className="assign-dropdown-list">
            <div className="assign-dropdown-search-user">
              <IoSearch className="assign-search-icon" />
              <input
                type="text"
                placeholder="Search"
                value={assignUserSearchValue}
                onChange={(e) => setAssignUserSearchValue(e.target.value)}
              />
            </div>

            <div className="assign-dropdown-user-options">
              <div className="assign-dropdown-select-all-option">
                <label>
                  <input
                    type="checkbox"
                    checked={
                        filteredAssignedUsers.length > 0 &&
                      selectedUsers.length === filteredAssignedUsers.length
                    }
                    onChange={handleSelectAllUsers}
                  />
                  <span>Select All</span>
                </label>
              </div>

              {filteredAssignedUsers.map((user) => (
                <div key={user.userId} className="assign-user-dropdown-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.userId)}
                      onChange={() => handleUserSelection(user.userId)}
                    />
                    <span>
                      {user.firstName} {user.lastName}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AssignUserDropdown;
